import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/common/Reveal";
import { SectionLabel } from "../components/common/SectionLabel";
import { TechCard } from "../components/common/TechCard";
import { useSection } from "../context/SiteContentContext";
import SEO from "../components/common/SEO";

const DEFAULT_CASES = [
    {
        id: "flood",
        tag: "GeoAI · Hazard",
        client: "U.S. Army Corps of Engineers",
        title: "Flood Modeling with Ensemble ML",
        body: "Developed ensemble ML models integrating slope, curvature, SPI, TWI, Euclidean distance to river, geology, LULC, soil and surface runoff.",
        metric: "97%",
        metric_label: "accuracy",
    },
];

function slugify(s = "") {
    return s
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .trim()
        .replace(/[\s_-]+/g, "-")
        .slice(0, 80);
}

export default function CaseStudies() {
    const CASES = useSection("case_studies", DEFAULT_CASES);
    return (
        <>
            <SEO
                title="Case Studies — Fieldwork, Not Demos · Skovomax"
                description="Deployed work with U.S. Army Corps of Engineers, NASA, and utility operators: 97% flood prediction accuracy, UAVSAR levee failure modeling, LiDAR corridor analysis, wildfire ignition and COVID-19 spatial modeling."
            />
            <section className="pt-28 pb-16 border-b border-white/10 relative overflow-hidden">
                <div className="absolute inset-0 photonic-grid opacity-20 pointer-events-none" />
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <Reveal>
                        <SectionLabel label="Case Studies / 04" />
                        <h1 className="font-serif-display text-6xl md:text-8xl leading-[0.95] tracking-tight max-w-5xl">
                            Fieldwork, not demos.
                        </h1>
                        <p className="mt-8 max-w-2xl text-white/60 text-lg leading-relaxed">
                            Selected projects from research, government and industry engagements — each built
                            end-to-end: sensor integration, modeling, validation, and delivery.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="py-24">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-6">
                    {CASES.map((c, i) => {
                        const slug = c.slug || slugify(c.title || c.id || "");
                        return (
                            <Reveal delay={i * 80} key={c.id}>
                                <Link
                                    to={`/case-studies/${slug}`}
                                    className="block h-full group"
                                    data-testid={`case-${c.id}`}
                                >
                                    <TechCard className="!p-10 h-full hover:border-[var(--accent-indigo)]/50 transition-colors">
                                        <div className="flex items-center justify-between mb-6">
                                            <div className="chip">{c.tag}</div>
                                            <div className="font-mono-tech text-[11px] tracking-[0.2em] text-white/50 uppercase">
                                                {c.client}
                                            </div>
                                        </div>
                                        <h3 className="font-serif-display text-3xl leading-tight mb-4">{c.title}</h3>
                                        <p className="text-white/60 leading-relaxed mb-8 line-clamp-4">{c.body}</p>
                                        <div className="pt-6 border-t border-white/10 flex items-baseline justify-between gap-3">
                                            <div className="flex items-baseline gap-3">
                                                <div className="font-serif-display text-5xl text-[var(--accent-mercury)]">
                                                    {c.metric}
                                                </div>
                                                <div className="font-mono-tech text-[11px] tracking-[0.2em] uppercase text-white/50">
                                                    {c.metric_label || c.metricLabel}
                                                </div>
                                            </div>
                                            <span className="font-mono-tech text-[10.5px] tracking-[0.2em] uppercase text-white/45 inline-flex items-center gap-1 group-hover:text-[var(--accent-indigo)] transition-colors">
                                                Read case <ArrowUpRight size={13} />
                                            </span>
                                        </div>
                                    </TechCard>
                                </Link>
                            </Reveal>
                        );
                    })}
                </div>
            </section>
        </>
    );
}
